/**
 * ConsentRequestModal – shows incoming Medical/Operator consent requests (REQ-008)
 * Appears automatically when a QR-scan triggered access request arrives via SSE
 */
import type { ConsentRequest } from "@/hooks/useConsentRequests";
import { useI18nStore } from "@/stores/i18nStore";
import { useEffect, useState } from "react";

interface Props {
  request: ConsentRequest;
  onGrant: (id: string) => void;
  onDeny: (id: string) => void;
  responding: boolean;
}

function getRoleIcon(role: string): string {
  return role === "MEDICAL" ? "🏥" : "✈️";
}

function getAccessLevelLabel(level: string, t: Record<string, string>): string {
  return (
    {
      EMERGENCY: t["consentAccessEmergency"] ?? "Emergency Data",
      MEDICAL: t["consentAccessMedical"] ?? "Full Medical Data",
      OPERATOR: t["consentAccessOperator"] ?? "Travel Documents",
    }[level] ?? level
  );
}

function useCountdown(expiresAt: string): number {
  const getRemaining = () =>
    Math.max(
      0,
      Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000),
    );

  const [seconds, setSeconds] = useState(getRemaining);

  useEffect(() => {
    const interval = setInterval(() => setSeconds(getRemaining()), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return seconds;
}

export function ConsentRequestModal({
  request,
  onGrant,
  onDeny,
  responding,
}: Props) {
  const { t } = useI18nStore();
  const secondsLeft = useCountdown(request.expiresAt);

  const isUrgent = secondsLeft < 60;
  const formattedTime =
    secondsLeft >= 60
      ? `${Math.floor(secondsLeft / 60)}m ${secondsLeft % 60}s`
      : `${secondsLeft}s`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">
              {getRoleIcon(request.requesterRole)}
            </span>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">
                {(t as unknown as Record<string, string>)[
                  "consentRequestTitle"
                ] ?? "Data Access Request"}
              </h2>
              <p className="text-blue-100 text-sm">
                {(t as unknown as Record<string, string>)[
                  "consentRequestSubtitle"
                ] ?? "Someone is requesting access to your health data"}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Requester info */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {(t as unknown as Record<string, string>)[
                  "consentRequesterName"
                ] ?? "Name"}
              </span>
              <span className="font-semibold text-gray-800">
                {request.requesterName}
              </span>
            </div>
            {request.requesterInstitution && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {(t as unknown as Record<string, string>)[
                    "consentRequesterInstitution"
                  ] ?? "Institution"}
                </span>
                <span className="font-semibold text-gray-800">
                  {request.requesterInstitution}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {(t as unknown as Record<string, string>)[
                  "consentAccessLevel"
                ] ?? "Access to"}
              </span>
              <span className="font-semibold text-blue-700">
                {getAccessLevelLabel(
                  request.accessLevel,
                  t as unknown as Record<string, string>,
                )}
              </span>
            </div>
          </div>

          {/* Countdown */}
          <div
            className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg ${
              isUrgent ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
            }`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium">
              {(t as unknown as Record<string, string>)[
                "consentRequestExpires"
              ] ?? "Expires in"}
              : <strong>{formattedTime}</strong>
            </span>
          </div>

          {secondsLeft === 0 && (
            <p className="text-sm text-center text-gray-500 italic">
              {(t as unknown as Record<string, string>)[
                "consentRequestExpiredInfo"
              ] ?? "This request has expired and will be automatically denied."}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={() => onDeny(request.id)}
            disabled={responding || secondsLeft === 0}
            className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold
                       hover:border-red-300 hover:bg-red-50 hover:text-red-700
                       disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            ❌{" "}
            {(t as unknown as Record<string, string>)["consentDeny"] ?? "Deny"}
          </button>
          <button
            onClick={() => onGrant(request.id)}
            disabled={responding || secondsLeft === 0}
            className="flex-1 py-3 px-4 rounded-xl bg-green-600 text-white font-semibold
                       hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all shadow-md"
          >
            ✅{" "}
            {(t as unknown as Record<string, string>)["consentGrant"] ??
              "Grant Access"}
          </button>
        </div>
      </div>
    </div>
  );
}
